/**
 * Knowledgebase Hub view + sort preferences, per user, in browser storage (a
 * convenience: it doesn't follow the user to another device). Every access is
 * wrapped so private windows / blocked storage just mean "defaults".
 */
import type { KbOrder, KbSort } from '../services/kbApi';

export type KbView = 'overview' | 'list';
/** Sorts offered while searching (relevance only makes sense with a query). */
export type KbSearchSort = 'relevance' | 'modified' | 'title';

export interface KbListSort {
  sort: Exclude<KbSort, 'relevance'>;
  order: KbOrder;
}

export interface KbPrefs {
  view: KbView;
  listSort: KbListSort;
  searchSort: KbSearchSort;
}

export const DEFAULT_KB_PREFS: KbPrefs = {
  view: 'overview',
  listSort: { sort: 'modified', order: 'desc' },
  searchSort: 'relevance',
};

/** "Browse all" sort menu: value = `${sort}:${order}`. */
export const LIST_SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'title:asc', label: 'Title (A–Z)' },
  { value: 'title:desc', label: 'Title (Z–A)' },
  { value: 'modified:desc', label: 'Last updated (newest)' },
  { value: 'modified:asc', label: 'Last updated (oldest)' },
  { value: 'folder:asc', label: 'Folder' },
  { value: 'type:asc', label: 'Type' },
];

export const SEARCH_SORT_OPTIONS: { value: KbSearchSort; label: string }[] = [
  { value: 'relevance', label: 'Best match' },
  { value: 'modified', label: 'Last updated' },
  { value: 'title', label: 'Title (A–Z)' },
];

export function parseListSort(value: string): KbListSort | null {
  return LIST_SORT_OPTIONS.some((o) => o.value === value)
    ? ({ sort: value.split(':')[0], order: value.split(':')[1] } as KbListSort)
    : null;
}

function key(userId: string): string {
  return `ow.kb.prefs.${userId}`;
}

export function readKbPrefs(userId: string | undefined): KbPrefs {
  if (!userId) return DEFAULT_KB_PREFS;
  try {
    const raw = localStorage.getItem(key(userId));
    const parsed = raw ? (JSON.parse(raw) as Partial<KbPrefs>) : {};
    const listSort = parsed.listSort && parseListSort(`${parsed.listSort.sort}:${parsed.listSort.order}`);
    return {
      view: parsed.view === 'list' ? 'list' : 'overview',
      listSort: listSort || DEFAULT_KB_PREFS.listSort,
      searchSort: SEARCH_SORT_OPTIONS.some((o) => o.value === parsed.searchSort)
        ? (parsed.searchSort as KbSearchSort)
        : DEFAULT_KB_PREFS.searchSort,
    };
  } catch {
    return DEFAULT_KB_PREFS;
  }
}

export function writeKbPrefs(userId: string | undefined, prefs: KbPrefs): void {
  if (!userId) return;
  try {
    localStorage.setItem(key(userId), JSON.stringify(prefs));
  } catch {
    /* storage unavailable */
  }
}
